import {
  Component,
  DestroyRef,
  ElementRef,
  inject,
  OnInit,
  Renderer2,
  signal,
  ViewChild,
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { NgClass } from '@angular/common';
import { LayoutService } from '../layout-service';
import { TranslateModule } from '@ngx-translate/core';
import { Translation } from '../../../core/services/translation/translation';
import { StorageService } from '../../../core/services/storage/storage';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DelegatedUIErrorI } from '../../shared/interfaces/delegated-ui-error.interface';
import { Apis } from '../../../core/services/apis/apis';
import { Router } from '@angular/router';
import { ToastService } from '../../../core/services/toast/toast.service';
import { UserI } from '../../shared/interfaces/user.interface';
import { Session } from '../../../core/services/session/session';

@Component({
  selector: 'app-header',
  imports: [NgClass, TranslateModule],
  templateUrl: './header.html',
  styleUrl: './header.scss',
})
export class Header implements OnInit {
  @ViewChild('optionsContainer')
  public optionsContainer?: ElementRef<HTMLDivElement>;
  public readonly title = inject(Title);
  private readonly layoutService = inject(LayoutService);
  private readonly sessionService = inject(Session);
  private readonly translation = inject(Translation);
  private readonly storageService = inject(StorageService);
  private readonly toastService = inject(ToastService);
  private readonly apiService = inject(Apis);
  private readonly router = inject(Router);
  private destroyRef = inject(DestroyRef);

  private renderer = inject(Renderer2);

  private element: ElementRef<HTMLDivElement> = inject(ElementRef);

  isDropdownOpen = signal<boolean>(false);
  itemBeingHovered: string | null = null;
  logoutIcon = signal<string>('svgs/icons/inactive-logout.svg');
  loggedInUser = signal<UserI | null>(null);

  langIcon: string =
    this.storageService.language === 'en'
      ? 'svgs/icons/auth-en.svg'
      : 'svgs/icons/auth-ar.svg';

  ngOnInit(): void {
    this.initLoggedInUser();
    this.focusListener();
  }

  initLoggedInUser() {
    this.sessionService
      .getLoggedInUser$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (loggedInUser) => {
          this.loggedInUser.set(loggedInUser);
        },
      });
  }

  toggleSideMenu() {
    this.layoutService.isSideMenuCollapsed.update(
      () => !this.layoutService.isSideMenuCollapsed()
    );
  }

  translate() {
    this.translation.changeLanguage();
    this.storageService.language === 'en'
      ? (this.langIcon = 'svgs/icons/auth-en.svg')
      : (this.langIcon = 'svgs/icons/auth-ar.svg');
  }
  itemOnHover(item: string | null) {
    this.itemBeingHovered = item;
    this.itemBeingHovered === 'logout'
      ? this.logoutIcon.update(() => 'svgs/icons/active-logout.svg')
      : this.logoutIcon.update(() => 'svgs/icons/inactive-logout.svg');
  }

  focusListener(): void {
    const dropdownContainer =
      this.element.nativeElement.children[0].children[1].children[2];
    this.renderer.listen('document', 'click', (event: MouseEvent) => {
      const { target } = event;
      const dropdownItems = dropdownContainer?.children?.[1]?.children;

      const isClickInsideComponent = dropdownContainer.contains(target as Node);

      let isTargetDropDownItem = false;
      const isOptionsContainerClicked =
        this.optionsContainer?.nativeElement.contains(target as Node);

      for (const dropDownItem of dropdownItems || []) {
        isTargetDropDownItem =
          dropDownItem.contains(target as Node) || dropDownItem === target;
        if (isTargetDropDownItem) break;
      }

      if (isOptionsContainerClicked) {
        this.isDropdownOpen.update(() => !this.isDropdownOpen());
      } else if (isClickInsideComponent && !isTargetDropDownItem) {
        this.isDropdownOpen.update(() => true);
      } else if (!isClickInsideComponent || isTargetDropDownItem) {
        this.isDropdownOpen.update(() => false);
      }
    });
  }

  extractInitials(name: string) {
    name = (name ?? 'guest user').trim().toUpperCase();
    return name[0] + ' ' + name[name.lastIndexOf(' ') + 1];
  }

  logout() {
    this.apiService
      .delete('auth/logout', {})
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.storageService.removeItem('accessToken');
          this.router.navigate(['auth/login']);
        },
        error: (err: DelegatedUIErrorI) => {
          this.toastService.showError(err.description, err.title);
          this.router.navigate(['auth/login']);
        },
      });
  }
}
